import React, { useState, useEffect, useCallback } from 'react';
import MaterialTable from 'material-table';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Avatar,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { colors } from '../components/Theme/WebTheme';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Button from "components/CustomButtons/Button.js";
import SubscriptionAPI from '../common/subscriptionApi';
import { useTranslation } from "react-i18next";
import { getStatusColor, getStatusBackgroundColor, getStatusTextColor, formatStatus, formatPaymentMethod } from '../common/subscriptionUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterItem: {
    minWidth: 200,
  },
  summaryCard: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  summaryText: {
    fontWeight: 'bold',
    color: colors.MAIN_COLOR,
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: 0,
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(3),
    borderBottom: '1px solid #e0e0e0',
  },
  modalContent: {
    padding: theme.spacing(3),
  },
  receiptPlaceholder: {
    width: '100%',
    height: 400,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #ccc',
  },
  placeholderIcon: {
    fontSize: 64,
    color: '#ccc',
  },
  actionButton: {
    minWidth: 'auto',
    padding: '4px 8px',
    margin: '0 2px',
    fontSize: '0.75rem'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  }
}));

export default function SubscriptionTransactions() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserType, setFilterUserType] = useState('');
  const [filterReceiptStatus, setFilterReceiptStatus] = useState('');
  const [filterDateRange, setFilterDateRange] = useState('');
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [allReceipts, setAllReceipts] = useState([]);
  const [currentReceiptIndex, setCurrentReceiptIndex] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString();
  }, []);

  const groupTransactionsBySubscription = useCallback((transactions) => {
    // Group by subscriptionId if available, otherwise create key from userId + planId for similar transactions
    const grouped = transactions.reduce((groups, transaction) => {
      const key = transaction.subscriptionId || `${transaction.userId}-${transaction.planId}`;

      if (!groups[key]) {
        groups[key] = {
          subscriptionGroup: key,
          user: transaction.user,
          planId: transaction.planId,
          planName: transaction.planName || transaction.plan,
          paymentMethod: transaction.paymentMethod,
          transactions: []
        };
      }
      groups[key].transactions.push(transaction);
      return groups;
    }, {});

    // Convert to array and add summary data
    return Object.values(grouped).map(group => {
      const sortedTransactions = group.transactions.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      );

      const totalAmount = group.transactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

      const latestTransaction = sortedTransactions[0];
      const dateRange = sortedTransactions.length > 1
        ? `${formatDate(sortedTransactions[sortedTransactions.length - 1].timestamp)} - ${formatDate(latestTransaction.timestamp)}`
        : formatDate(latestTransaction.timestamp);

      return {
        ...group,
        date: dateRange,
        amount: totalAmount,
        status: latestTransaction.status,
        displayStatus: latestTransaction.displayStatus || latestTransaction.status,
        transactionCount: group.transactions.length,
        latestTransaction,
        plan: group.planName || group.planId,
        receipt: group.transactions.some(t => t.receiptUrl) ? 'Available' : 'N/A'
      };
    });
  }, [formatDate]);

  // Helper function to fetch missing user data
  const fetchMissingUserData = async (transactions) => {
    console.log(`🔍 fetchMissingUserData called with ${transactions.length} transactions`);
    const updatedTransactions = [...transactions];
    
    for (let i = 0; i < updatedTransactions.length; i++) {
      const transaction = updatedTransactions[i];
      
      // Check if user data is missing or incomplete
      if (!transaction.user?.name || 
          transaction.user.name === "Unknown User" || 
          transaction.user.name === t("unknown_user") ||
          transaction.user.name === "Usuario desconocido") {
        
        // Skip if userId contains invalid characters for Firebase Database
        if (transaction.userId && (transaction.userId.includes('.') || transaction.userId.includes('#') || transaction.userId.includes('$') || transaction.userId.includes('[') || transaction.userId.includes(']'))) {
          console.log(`⚠️ Skipping user data fetch for ${transaction.userId} - contains invalid characters`);
          continue;
        }
        
        try {
          console.log(`🔍 Fetching user data for ${transaction.userId} from users collection...`);
          const userResponse = await fetch(
            `${SubscriptionAPI.getBaseUrl()}/getuser?userId=${transaction.userId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            console.log(`📡 Raw response for ${transaction.userId}:`, userData);
            if (userData.success && userData.user) {
              console.log(`✅ Found user data for ${transaction.userId}:`, userData.user);
              // Combine firstName and lastName if available
              let fullName = userData.user.name;
              if (!fullName && (userData.user.firstName || userData.user.lastName)) {
                if (userData.user.firstName && userData.user.lastName) {
                  fullName = `${userData.user.firstName} ${userData.user.lastName}`;
                } else if (userData.user.firstName) {
                  fullName = userData.user.firstName;
                } else if (userData.user.lastName) {
                  fullName = userData.user.lastName;
                }
              }

              updatedTransactions[i] = {
                ...transaction,
                user: {
                  ...transaction.user,
                  name: fullName || transaction.user?.name || t("unknown_user"),
                  email: userData.user.email || transaction.user?.email || "",
                  phone: userData.user.phone || transaction.user?.phone || "",
                  type: userData.user.type || transaction.user?.type || "customer",
                  avatar: fullName ? fullName.charAt(0).toUpperCase() : 'U'
                }
              };
            } else {
              console.warn(`⚠️ No user data found for ${transaction.userId}`);
            }
          } else {
            console.warn(`⚠️ Failed to fetch user data for ${transaction.userId}:`, userResponse.status);
          }
        } catch (error) {
          console.error(`❌ Error fetching user data for ${transaction.userId}:`, error);
        }
      }
    }
    
    return updatedTransactions;
  };

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);

      const filters = {
        paymentMethod: filterPaymentMethod,
        status: filterStatus,
        userType: filterUserType,
        receiptStatus: filterReceiptStatus,
        dateRange: filterDateRange
      };

      // Convert receipt status filter to backend format
      if (filterReceiptStatus === 'has_receipt') {
        filters.hasReceipt = true;
      } else if (filterReceiptStatus === 'no_receipt') {
        filters.hasReceipt = false;
      }

      console.log('🔍 Frontend filters state:', {
        filterPaymentMethod,
        filterStatus,
        filterUserType,
        filterReceiptStatus,
        filterDateRange
      });

      // Handle special case: if filtering by success, also include admin_update
      if (filterStatus === 'success') {
        filters.includeAdminUpdate = true;
      }

      const response = await SubscriptionAPI.getSubscriptionTransactions(filters);

      if (response.success) {
        // Map and format the data for MaterialTable
        const formattedTransactions = response.transactions.map(transaction => ({
          ...transaction,
          date: formatDate(transaction.timestamp),
          plan: transaction.planName || transaction.planId,
          receipt: transaction.receiptUrl ? 'Available' : 'N/A',
          // Handle special case: admin_update should be treated as success for filtering
          displayStatus: transaction.status === 'admin_update' ? 'success' : transaction.status
        }));

        console.log('Loaded transactions:', formattedTransactions.length);
        console.log('Sample transaction:', formattedTransactions[0]);

        // Fetch missing user data
        console.log(`🔄 About to call fetchMissingUserData with ${formattedTransactions.length} transactions`);
        const updatedTransactions = await fetchMissingUserData(formattedTransactions);
        console.log(`✅ fetchMissingUserData completed, processing ${updatedTransactions.length} transactions`);

        // Group transactions by subscription/user
        const groupedTransactions = groupTransactionsBySubscription(updatedTransactions);
        setTransactions(groupedTransactions);
      } else {
        console.log('Failed to load transactions');
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [filterPaymentMethod, filterStatus, filterUserType, filterReceiptStatus, filterDateRange, formatDate, groupTransactionsBySubscription]);

  useEffect(() => {
    loadTransactions();
  }, [filterPaymentMethod, filterStatus, filterUserType, filterReceiptStatus, filterDateRange, loadTransactions]);





  const handleViewReceipt = (transaction) => {
    setSelectedTransaction(transaction);
    setReceiptModalOpen(true);
  };

  const handleViewAllReceipts = (transactions) => {
    // Find all transactions with receipts
    const transactionsWithReceipts = transactions.filter(t => t.receiptUrl);
    if (transactionsWithReceipts.length > 0) {
      setAllReceipts(transactionsWithReceipts);
      setCurrentReceiptIndex(0);
      setSelectedTransaction(transactionsWithReceipts[0]); // Show first receipt
      setReceiptModalOpen(true);
    }
  };

  const handleToggleActive = (transaction) => {
    const newStatus = !transaction.isActive;
    const targetStatus = newStatus ? 'active' : 'cancelled';

    setPendingStatusChange({
      transaction,
      newStatus,
      targetStatus
    });
    setConfirmDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    const { transaction, newStatus, targetStatus } = pendingStatusChange;

    try {
      console.log('Toggle active for transaction:', transaction);
      console.log(`Updating transaction ${transaction.id} for user ${transaction.userId} to status: ${targetStatus}`);

      // Update in database via API
      const result = await SubscriptionAPI.updateSubscriptionStatus(
        transaction.userId,
        targetStatus
      );

      console.log('API response:', result);

      if (result.success) {
        // Update local state only if API call succeeds
        setTransactions(prev => prev.map(t =>
          t.id === transaction.id
            ? { ...t, isActive: newStatus }
            : t
        ));

        // Show success message
        console.log(`Transaction ${newStatus ? 'activated' : 'deactivated'} successfully`);
      } else {
        console.error('Failed to update transaction status:', result);
        alert('Failed to update transaction status: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating transaction status:', error);
      console.error('Error details:', error.message, error.stack);
      alert('Error updating transaction status. Please try again. Details: ' + error.message);
    } finally {
      setConfirmDialogOpen(false);
      setPendingStatusChange(null);
    }
  };

  const handleRefresh = () => {
    loadTransactions();
  };

  const handleNextReceipt = () => {
    if (currentReceiptIndex < allReceipts.length - 1) {
      const nextIndex = currentReceiptIndex + 1;
      setCurrentReceiptIndex(nextIndex);
      setSelectedTransaction(allReceipts[nextIndex]);
    }
  };

  const handlePrevReceipt = () => {
    if (currentReceiptIndex > 0) {
      const prevIndex = currentReceiptIndex - 1;
      setCurrentReceiptIndex(prevIndex);
      setSelectedTransaction(allReceipts[prevIndex]);
    }
  };

  const handleDownloadReceipt = () => {
    if (selectedTransaction?.receiptUrl) {
      const link = document.createElement('a');
      link.target = '_blank';
      link.href = selectedTransaction.receiptUrl;
      link.download = `receipt_${selectedTransaction.id || Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const groupedColumns = [
    {
      title: t('date_range'),
      field: 'date',
      cellStyle: {
        textAlign: 'left',
        fontWeight: 'bold',
        fontSize: '0.875rem',
        color: '#333'
      },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem'
      }
    },
    {
      title: t('user'),
      field: 'user',
      render: rowData => (
        <Box display="flex" alignItems="center">
          <Avatar
            style={{
              backgroundColor: colors.MAIN_COLOR,
              marginRight: 12,
              width: 35,
              height: 35,
              fontSize: '0.875rem'
            }}
          >
            {rowData.user.name && rowData.user.name !== "Unknown User" ? 
              rowData.user.name.charAt(0).toUpperCase() : 
              (rowData.user.avatar || 'U')}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
              {rowData.user.name && rowData.user.name !== "Unknown User" ? rowData.user.name : t("unknown_user")}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666' }}>
              {rowData.user.type ? t(rowData.user.type.toLowerCase()) : 'Unknown'}
            </div>
          </div>
        </Box>
      ),
      cellStyle: { paddingLeft: 24 },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem'
      }
    },
    {
      title: t('subscriptions'),
      field: 'plan',
      render: rowData => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{rowData.plan}</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>
            {rowData.transactionCount} {t('transaction')}{rowData.transactionCount !== 1 ? 's' : ''}
          </div>
        </div>
      ),
      cellStyle: {
        fontWeight: 'bold',
        fontSize: '0.875rem'
      },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem'
      }
    },
    {
      title: t('total_amount'),
      field: 'amount',
      render: rowData => (
        <span style={{ fontWeight: 'bold', color: colors.MAIN_COLOR, fontSize: '0.875rem' }}>
          {formatAmount(rowData.amount)}
        </span>
      ),
      cellStyle: { textAlign: 'center' },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem',
        textAlign: 'center'
      }
    },
    {
      title: t('method'),
              field: 'paymentMethod',
        render: rowData => formatPaymentMethod(rowData.paymentMethod, t),
      cellStyle: {
        textAlign: 'center',
        fontSize: '0.875rem'
      },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem',
        textAlign: 'center'
      }
    },
    {
      title: t('latest_status'),
      field: 'status',
      render: rowData => (
        <span style={{
          backgroundColor: getStatusBackgroundColor(rowData.status),
          color: getStatusTextColor(rowData.status),
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 'bold'
        }}>
          {formatStatus(rowData.status, t)}
        </span>
      ),
      cellStyle: { textAlign: 'center' },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem',
        textAlign: 'center'
      }
    },
    {
      title: t('receipt'),
      field: 'receipt',
      render: rowData => {
        // Check if any transaction in the group has a receipt
        const hasReceipt = rowData.transactions.some(t => t.receiptUrl);
        const receiptCount = rowData.transactions.filter(t => t.receiptUrl).length;
        
        if (hasReceipt) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <span style={{
                backgroundColor: colors.MAIN_COLOR,
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                {receiptCount} {t('receipt')}{receiptCount > 1 ? 's' : ''}
              </span>
              <Button
                color="info"
                size="sm"
                className={classes.actionButton}
                onClick={() => handleViewAllReceipts(rowData.transactions)}
                style={{ fontSize: '0.7rem', padding: '2px 6px' }}
              >
                {t('view_receipt')}
              </Button>
            </div>
          );
        } else {
          return (
            <span style={{
              backgroundColor: '#f0f0f0',
              color: '#666',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem'
            }}>
              {t('not_available')}
            </span>
          );
        }
      },
      cellStyle: { textAlign: 'center' },
      headerStyle: {
        fontWeight: 'bold',
        color: '#666',
        fontSize: '0.875rem',
        textAlign: 'center'
      }
    }
  ];

  const detailColumns = [
    {
      title: t('date'),
      field: 'date',
      cellStyle: { fontSize: '0.75rem' },
      headerStyle: { fontSize: '0.75rem', fontWeight: 'bold' }
    },
    {
      title: t('amount'),
      field: 'amount',
      render: rowData => (
        <span style={{ fontWeight: 'bold', color: colors.MAIN_COLOR, fontSize: '0.75rem' }}>
          {formatAmount(rowData.amount)}
        </span>
      ),
      cellStyle: { fontSize: '0.75rem' },
      headerStyle: { fontSize: '0.75rem', fontWeight: 'bold' }
    },
    {
      title: t('status'),
      field: 'displayStatus',
      render: rowData => (
        <span style={{
          backgroundColor: getStatusBackgroundColor(rowData.status),
          color: getStatusTextColor(rowData.status),
          padding: '2px 6px',
          borderRadius: '8px',
          fontSize: '0.7rem',
          fontWeight: 'bold'
        }}>
          {formatStatus(rowData.status, t)}
        </span>
      ),
      cellStyle: { fontSize: '0.75rem' },
      headerStyle: { fontSize: '0.75rem', fontWeight: 'bold' }
    },
    {
      title: t('receipt'),
      field: 'receipt',
      render: rowData => {
        if (rowData.receiptUrl) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
              <span style={{
                backgroundColor: colors.MAIN_COLOR,
                color: 'white',
                padding: '2px 6px',
                borderRadius: '8px',
                fontSize: '0.65rem',
                fontWeight: 'bold'
              }}>
                {t('available')}
              </span>
              <Button
                color="info"
                size="sm"
                className={classes.actionButton}
                onClick={() => handleViewReceipt(rowData)}
                style={{ fontSize: '0.65rem', padding: '1px 4px' }}
              >
                {t('view')}
              </Button>
            </div>
          );
        } else {
          return (
            <span style={{
              backgroundColor: '#f0f0f0',
              color: '#666',
              padding: '2px 6px',
              borderRadius: '8px',
              fontSize: '0.65rem'
            }}>
              {t('not_available')}
            </span>
          );
        }
      },
      cellStyle: { fontSize: '0.75rem', width: 100 },
      headerStyle: { fontSize: '0.75rem', fontWeight: 'bold' }
    },
    {
      title: t('actions'),
      field: 'actions',
      sorting: false,
      render: rowData => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <Button
            color={rowData.isActive ? "danger" : "success"}
            size="sm"
            className={classes.actionButton}
            onClick={() => handleToggleActive(rowData)}
            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
          >
            {rowData.isActive ? t('deactivate') : t('activate')}
          </Button>
        </div>
      ),
      cellStyle: { fontSize: '0.75rem', width: 100 },
      headerStyle: { fontSize: '0.75rem', fontWeight: 'bold' }
    }
  ];

  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          {t('payment_transactions')}
        </Typography>
        <Button
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          {t('refresh')}
        </Button>
      </Box>

      <div className={classes.filterContainer}>
        <div className={classes.filterRow}>
          <FormControl className={classes.filterItem} size="small">
            <InputLabel>{t('payment_method')}</InputLabel>
            <Select
              value={filterPaymentMethod}
              label={t('payment_method')}
              onChange={(e) => setFilterPaymentMethod(e.target.value)}
            >
              <MenuItem value="">{t('all_methods')}</MenuItem>
              <MenuItem value="wallet">{t('wallet')}</MenuItem>
              <MenuItem value="cash">{t('cash')}</MenuItem>
              <MenuItem value="free">{t('free')}</MenuItem>
              <MenuItem value="yappi">{t('yappi')}</MenuItem>
              <MenuItem value="bank_transfer">{t('bank_transfer')}</MenuItem>
              <MenuItem value="stripe">{t('stripe')}</MenuItem>
              <MenuItem value="paypal">{t('paypal')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterItem} size="small">
            <InputLabel>{t('status')}</InputLabel>
            <Select
              value={filterStatus}
              label={t('status')}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">{t('all_status')}</MenuItem>
              <MenuItem value="active">{t('active')}</MenuItem>
              <MenuItem value="pending">{t('pending')}</MenuItem>
              <MenuItem value="payment_pending">{t('payment_pending')}</MenuItem>
              <MenuItem value="expired">{t('expired')}</MenuItem>
              <MenuItem value="cancelled">{t('cancelled')}</MenuItem>
              <MenuItem value="success">{t('success')}</MenuItem>
              <MenuItem value="failed">{t('failed')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterItem} size="small">
            <InputLabel>{t('user_type')}</InputLabel>
            <Select
              value={filterUserType}
              label={t('user_type')}
              onChange={(e) => setFilterUserType(e.target.value)}
            >
              <MenuItem value="">{t('all_user_types')}</MenuItem>
              <MenuItem value="customer">{t('users')}</MenuItem>
              <MenuItem value="driver">{t('driver')}</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className={classes.filterRow}>
          <FormControl className={classes.filterItem} size="small">
            <InputLabel>{t('receipt_status')}</InputLabel>
            <Select
              value={filterReceiptStatus}
              label={t('receipt_status')}
              onChange={(e) => setFilterReceiptStatus(e.target.value)}
            >
              <MenuItem value="">{t('all_status')}</MenuItem>
              <MenuItem value="has_receipt">{t('has_receipt')}</MenuItem>
              <MenuItem value="no_receipt">{t('no_receipt')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterItem} size="small">
            <InputLabel>{t('date_range')}</InputLabel>
            <Select
              value={filterDateRange}
              label={t('date_range')}
              onChange={(e) => setFilterDateRange(e.target.value)}
            >
              <MenuItem value="">{t('all_dates')}</MenuItem>
              <MenuItem value="today">{t('today')}</MenuItem>
              <MenuItem value="week">{t('this_week')}</MenuItem>
              <MenuItem value="month">{t('this_month')}</MenuItem>
              <MenuItem value="year">{t('this_year')}</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <Box className={classes.summaryCard}>
        <Typography variant="h6" className={classes.summaryText}>
          {t('grouped_payment_transactions')}
        </Typography>
        <Typography variant="body2" style={{ color: '#666', marginTop: 4 }}>
          {transactions.length} {t('subscription_groups')} • {transactions.reduce((total, group) => total + group.transactionCount, 0)} {t('total_transactions')} • {
            transactions.reduce((total, group) => total + group.transactions.filter(t => t.receiptUrl).length, 0)
          } {t('receipts_available')}
        </Typography>
      </Box>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <MaterialTable
          title=""
          columns={groupedColumns}
          data={transactions}
          detailPanel={(rowData) => (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" style={{ marginBottom: '12px', fontWeight: 'bold' }}>
                {t('transaction_details')} - {rowData.plan}
              </Typography>
              <MaterialTable
                title=""
                columns={detailColumns}
                data={rowData.transactions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))}
                options={{
                  search: false,
                  paging: false,
                  toolbar: false,
                  showTitle: false,
                  rowStyle: {
                    fontSize: '0.75rem'
                  },
                  headerStyle: {
                    fontWeight: 'bold',
                    backgroundColor: '#f0f0f0',
                    fontSize: '0.75rem'
                  }
                }}
              />
            </div>
          )}
          options={{
            search: false,
            paging: true,
            pageSize: 10,
            pageSizeOptions: [5, 10, 20, 50],
            showTitle: false,
            toolbar: false,
            filtering: false,
            exportButton: false,
            selection: false,
            headerStyle: {
              backgroundColor: '#f0f0f0',
              fontWeight: 'bold',
              borderBottom: '1px solid #e0e0e0'
            },
            rowStyle: {
              fontSize: '0.875rem'
            }
          }}
        />
      )}

      {/* Receipt Modal */}
      <Modal
        open={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        className={classes.modal}
      >
        <div className={classes.modalPaper}>
          <div className={classes.modalHeader}>
            <Typography variant="h6">
              {t('cash_payment_receipt')}
              {allReceipts.length > 1 && (
                <span style={{ fontSize: '0.875rem', color: '#666', marginLeft: 8 }}>
                  ({currentReceiptIndex + 1} of {allReceipts.length})
                </span>
              )}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {allReceipts.length > 1 && (
                <>
                  <IconButton 
                    onClick={handlePrevReceipt}
                    disabled={currentReceiptIndex === 0}
                    size="small"
                  >
                    <span style={{ fontSize: '1.2rem' }}>‹</span>
                  </IconButton>
                  <IconButton 
                    onClick={handleNextReceipt}
                    disabled={currentReceiptIndex === allReceipts.length - 1}
                    size="small"
                  >
                    <span style={{ fontSize: '1.2rem' }}>›</span>
                  </IconButton>
                </>
              )}
              <IconButton onClick={() => setReceiptModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <div className={classes.modalContent}>
            {selectedTransaction?.receiptUrl ? (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={selectedTransaction.receiptUrl}
                  alt={t('payment_proof')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{ display: 'none', textAlign: 'center', padding: '50px' }}>
                  <ReceiptIcon className={classes.placeholderIcon} />
                  <Typography variant="body1" style={{ marginTop: 16, color: '#999' }}>
                    {t('receipt_not_available')}
                  </Typography>
                </div>
                
                {selectedTransaction && (
                  <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Typography variant="h6" style={{ color: colors.MAIN_COLOR }}>
                        {t('transaction_details')}
                      </Typography>
                      <Button
                        color="info"
                        size="sm"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadReceipt}
                        style={{ fontSize: '0.75rem' }}
                      >
                        {t('download')}
                      </Button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', textAlign: 'left' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {t('user')}:
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.user?.name || 'N/A'}
                      </Typography>
                      
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {t('amount')}:
                      </Typography>
                      <Typography variant="body2" style={{ color: colors.MAIN_COLOR, fontWeight: 'bold' }}>
                        {formatAmount(selectedTransaction.amount)}
                      </Typography>
                      
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {t('payment_method')}:
                      </Typography>
                      <Typography variant="body2">
                        {formatPaymentMethod(selectedTransaction.paymentMethod, t)}
                      </Typography>
                      
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {t('status')}:
                      </Typography>
                      <Typography variant="body2">
                        {formatStatus(selectedTransaction.status, t)}
                      </Typography>
                      
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {t('date')}:
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.date}
                      </Typography>
                      
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {t('plan')}:
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.planName || selectedTransaction.planId}
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={classes.receiptPlaceholder}>
                <div style={{ textAlign: 'center' }}>
                  <ReceiptIcon className={classes.placeholderIcon} />
                  <Typography variant="body1" style={{ marginTop: 16, color: '#999' }}>
                    {t('receipt_not_available')}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('confirm_status_change')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('are_you_sure_change_status')} "{pendingStatusChange?.targetStatus}"?
            <br />
            {t('this_action_cannot_be_undone')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            color="transparent"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmStatusChange}
            color="primary"
            autoFocus
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
} 